import { FeotClockMode } from '../qa-tools-service/feot-clock.interface';

export type ElapsedUnit = 'hours' | 'days' | 'weeks';

export interface IElapsedOption {
  label: string;
  value: string;
}

export interface IElapsedUnitOption {
  label: string;
  value: ElapsedUnit;
}

export interface IFeotClockModeOption {
  key: FeotClockMode;
  label: string;
  hint: string;
}

export interface ISnapshotRow {
  /** Dotted path into the object being rendered. */
  key: string;
  label: string;
  isTimestamp?: boolean;
}

export interface ISnapshotGroup {
  /** Stable id, used as the collapsed-state key. */
  key: string;
  title: string;
  rows: ISnapshotRow[];
}

/**
 * ELAPSED_ALIASES of helpers/feot-clock/elapsed-input.js. Every one of them is a
 * percentage of the planned field window, not a fixed duration.
 */
export const ELAPSED_OPTIONS: IElapsedOption[] = [
  { label: 'start - 0% of field time', value: 'start' },
  { label: 'mid - 50% of field time', value: 'mid' },
  { label: 'near-end - 90% of field time', value: 'near-end' },
  { label: 'end - 100% of field time', value: 'end' },
  { label: 'overdue - 110% of field time', value: 'overdue' },
];

export const ELAPSED_UNITS: IElapsedUnitOption[] = [
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
  { label: 'Weeks', value: 'weeks' },
];

/** The endpoint knows no elapsedDays or elapsedWeeks key: the unit is resolved here. */
export const ELAPSED_UNIT_TO_HOURS: Record<ElapsedUnit, number> = {
  hours: 1,
  days: 24,
  weeks: 24 * 7,
};

/**
 * The endpoint resolves surveyLaunchTS > elapsedHours > elapsed. The form sends
 * only the selected one so the precedence is never applied silently.
 */
export const FEOT_CLOCK_MODES: IFeotClockModeOption[] = [
  {
    key: 'elapsed',
    label: 'Elapsed field time',
    hint: 'A named point on the pacing curve, relative to the planned field window. Sent as elapsed.',
  },
  {
    key: 'elapsedHours',
    label: 'Elapsed Survey Time in',
    hint: 'A fixed duration, converted to hours and sent as elapsedHours.',
  },
  {
    key: 'surveyLaunchTS',
    label: 'Survey launch timestamp',
    hint: 'Absolute launch timestamp in unix milliseconds. Sent as surveyLaunchTS.',
  },
];

export const SNAPSHOT_GROUPS: ISnapshotGroup[] = [
  {
    key: 'survey',
    title: 'Survey',
    rows: [
      { label: 'Survey status (st)', key: 'st' },
      { label: 'FEOT enabled', key: 'feot.enabled' },
      { label: 'Field time (days)', key: 'fldTime' },
    ],
  },
  {
    key: 'timing',
    title: 'Timing & pacing',
    rows: [
      { label: 'surveyLaunchTS', key: 'surveyLaunchTS', isTimestamp: true },
      { label: 'sr_launchTS', key: 'srLaunchTS', isTimestamp: true },
      { label: 'field_end_date', key: 'fieldEndDate', isTimestamp: true },
      { label: 'Elapsed since launch (hours)', key: 'elapsedHoursSinceLaunch' },
      {
        label: 'Fulfilled field time (pacing units)',
        key: 'fulfilledFieldTimeInPacingUnits',
      },
      {
        label: 'Planned field time (pacing units)',
        key: 'plannedFieldTimeInPacingUnits',
      },
      { label: 'pacing', key: 'feot.pacing' },
      { label: 'pacingInterval', key: 'feot.pacingInterval' },
    ],
  },
  {
    key: 'flags',
    title: 'Status & flags',
    rows: [
      { label: 'is_midway_survey', key: 'feot.is_midway_survey' },
      { label: 'isCustomPacing', key: 'feot.isCustomPacing' },
      { label: 'flexibility', key: 'feot.flexibility' },
    ],
  },
];

/** Rendered ungrouped: the pacing lambda writes these, this endpoint never does. */
export const LAMBDA_GROUPS: ISnapshotGroup[] = [
  {
    key: 'lambda',
    title: 'Written by the pacing lambda',
    rows: [
      { label: 'currentLiveTS', key: 'feot.currentLiveTS', isTimestamp: true },
      { label: 'nextCalcTS', key: 'feot.nextCalcTS', isTimestamp: true },
      { label: 'fftIntervals (count)', key: 'feot.fftIntervalsCount' },
    ],
  },
];

/** `pacing` reads from the composite label built by the component. */
export const RESPONSE_ROWS: ISnapshotRow[] = [
  { label: 'surveyId', key: 'surveyId' },
  { label: 'previousLaunchTS', key: 'previousLaunchTS', isTimestamp: true },
  { label: 'newLaunchTS', key: 'newLaunchTS', isTimestamp: true },
  { label: 'fieldEndDate', key: 'fieldEndDate', isTimestamp: true },
  { label: 'elapsedPercent', key: 'elapsedPercent' },
  { label: 'elapsedHours', key: 'elapsedHours' },
  { label: 'plannedFieldTimeHours', key: 'plannedFieldTimeHours' },
  { label: 'pacing', key: 'pacing' },
  {
    label: 'fulfilledFieldTimeInPacingUnits',
    key: 'fulfilledFieldTimeInPacingUnits',
  },
  {
    label: 'plannedFieldTimeInPacingUnits',
    key: 'plannedFieldTimeInPacingUnits',
  },
  { label: 'isMidwaySurvey', key: 'isMidwaySurvey' },
  { label: 'midwayFlagUpdated', key: 'midwayFlagUpdated' },
  { label: 'enqueued', key: 'enqueued' },
  { label: 'env', key: 'env' },
];

export const SURVEY_ID_HINT =
  'Must be live with FEOT enabled, otherwise the endpoint answers 409.';

export const MIDWAY_NOT_SENT_HINT =
  'Unchecked: the is_midway_survey key is omitted from the request and the stored flag is left untouched.';

export const TRIGGER_HINT =
  'Unchecked: the dates are stored but nothing is pushed to the FEOT queue.';

export const STANDARD_PATH_WARNING =
  'Survey is not flagged as midway: the launch dates were changed but the calculation will not be, ' +
  'because the standard path sums fftIntervals and never reads surveyLaunchTS.';

export const NOT_SIMULABLE_WARNING =
  'This survey is not live with FEOT enabled, so the update will be rejected with 409.';

export const COMPLETES_UNAVAILABLE_HINT =
  'This api_platform deploy does not return the completes, buyer and quota fields yet. ' +
  'Everything else on this page still works.';

export const PROJECTION_UNAVAILABLE_HINT =
  'Projection needs goal, fielded and a planned field window. One of them was not read for this survey.';

export const STANDARD_PATH_NOTE =
  'Cumulative allowance over the whole goal, using the fftIntervals sum as the clock';

export const MIDWAY_PATH_NOTE =
  "One interval's increment over the remaining goal, using now - surveyLaunchTS as the clock.";

export const PROJECTION_MATCHES_HINT =
  'The active path reproduces every stored current_target, so the last recalculation is fully accounted for.';

export const PROJECTION_DIFFERS_HINT =
  'Stored differs from the active path: the lambda has not run since the last change, ' +
  'or an input moved after it did.';

export const QUOTA_GROUP_NOTE =
  "Note that the lambda applies each quota group's percentages to the same project target, " +
  'so total Currently Open is multiplied by the number of independent groups.';

export const SNAPSHOT_CHANGED_HINT =
  'Highlighted rows changed with the last update. Teal marks a value, amber a timestamp.';

export const LAMBDA_ROWS_HINT =
  'Written by the pacing lambda, not by this endpoint. They only move once the ' +
  'queued recalculation has run - use Refresh then.';

export const NO_RESPONSE_HINT = 'No update run yet in this session.';

export const TIMESTAMP_FORMAT = 'MMM d, y, HH:mm:ss';
export const TIMESTAMP_TZ = 'UTC';

export const SURVEY_LOOKUP_DEBOUNCE_MS = 600;

export const DEFAULT_ELAPSED = 'mid';
export const DEFAULT_ELAPSED_UNIT: ElapsedUnit = 'hours';
